'use client';

/**
 * Storefront-Ankündigungs-Banner (F500-Pattern). Dismissbar (sessionStorage,
 * per Message-Hash → neue Nachricht erscheint wieder). Zeitfenster/Stil/CTA via
 * resolveAnnouncement. Opt-in; rendert nichts wenn inaktiv. RTL-aware.
 */

import { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { motion, useReducedMotion } from 'framer-motion';
import { X } from 'lucide-react';
import { AnnouncementIcon } from './AnnouncementIcon';
import {
  resolveAnnouncement,
  announcementHash,
  type AnnouncementStyle,
  type TenantAnnouncementFields,
} from '@/lib/storefront/announcement';

// Weiße Karte wie der restliche Storefront-Content — Farbe lebt NUR im tonalen
// Icon-Badge + CTA (dasselbe Muster wie die StatsCards). Kein Pastell-Fill, kein
// farbiger Accent-Balken (beides wirkt generisch).
const ACCENT: Record<AnnouncementStyle, { badge: string; link: string }> = {
  info: { badge: 'bg-blue-50 text-blue-600', link: 'text-blue-700' },
  offer: { badge: 'bg-emerald-50 text-emerald-600', link: 'text-emerald-700' },
  important: { badge: 'bg-amber-50 text-amber-600', link: 'text-amber-700' },
};

export function AnnouncementBanner({
  tenant,
}: {
  tenant: TenantAnnouncementFields & { id: string; timezone?: string | null };
}) {
  const t = useTranslations('storefront.announcement');
  const storageKey = `eb_ann_${tenant.id}`;
  const tz = tenant.timezone || undefined;
  // Dismiss-Key deckt Message + Stil + Link + Zeitfenster ab → jede inhaltliche
  // Änderung lässt den Banner wieder erscheinen (nicht nur Message-Änderung).
  const dismissKey = (a: ReturnType<typeof resolveAnnouncement>) =>
    announcementHash(
      a?.message ?? '',
      `${a?.style}|${a?.linkUrl ?? ''}|${tenant.announcement_starts_at ?? ''}|${tenant.announcement_ends_at ?? ''}`,
    );
  // Client-Zeit erst nach Mount erfassen → kein Hydration-Mismatch beim
  // Zeitfenster-Check und kein Date.now() im Render (impure).
  const [now, setNow] = useState<number | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const reduce = useReducedMotion();

  useEffect(() => {
    const t0 = Date.now();
    const a = resolveAnnouncement(tenant, t0, tz);
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setNow(t0);
    if (a) {
      try {
        if (window.sessionStorage.getItem(storageKey) === dismissKey(a)) setDismissed(true);
      } catch {
        /* Storage blockiert → Banner zeigen ist der sichere Default */
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant, storageKey, tz]);

  if (now === null || dismissed) return null;
  const ann = resolveAnnouncement(tenant, now, tz);
  if (!ann) return null;
  const hash = dismissKey(ann);

  const accent = ACCENT[ann.style];
  const onDismiss = () => {
    setDismissed(true);
    try {
      window.sessionStorage.setItem(storageKey, hash);
    } catch {
      /* egal */
    }
  };

  return (
    <motion.div
      role="status"
      initial={reduce ? false : { opacity: 0, y: -8 }}
      animate={reduce ? undefined : { opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: 'easeOut' }}
      className="flex items-center gap-3 rounded-2xl border border-gray-200/80 bg-white px-4 py-3 shadow-[0_1px_2px_rgba(0,0,0,0.04)]"
    >
      <span
        className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg ${accent.badge}`}
        aria-hidden="true"
      >
        <AnnouncementIcon style={ann.style} />
      </span>

      <div className="min-w-0 flex-1">
        <p className="whitespace-pre-line break-words text-sm font-medium leading-snug text-gray-900">
          {ann.message}
        </p>
        {ann.linkUrl && (
          <a
            href={ann.linkUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={`mt-0.5 inline-flex items-center gap-1 text-sm font-semibold hover:underline ${accent.link}`}
          >
            {ann.linkLabel || t('learnMore')}
            <span aria-hidden="true" className="rtl:-scale-x-100">→</span>
          </a>
        )}
      </div>

      <button
        type="button"
        onClick={onDismiss}
        aria-label={t('dismiss')}
        className="-me-1 flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 sm:h-8 sm:w-8"
      >
        <X className="h-4 w-4" strokeWidth={2} />
      </button>
    </motion.div>
  );
}
